import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Plus, Pencil, Trash2, Clock, CheckCircle, XCircle, Calendar, Mail, Bold, Italic, Underline, List, ListOrdered, Link2, AlignLeft, AlignCenter, AlignRight } from 'lucide-react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import TextAlign from '@tiptap/extension-text-align';
import UnderlineExt from '@tiptap/extension-underline';
import { format, parseISO } from 'date-fns';
import { de } from 'date-fns/locale';
import DOMPurify from 'dompurify';

// Rich Text Editor Component
const RichTextEditor = ({ content, onChange }) => {
  const editor = useEditor({
    extensions: [
      StarterKit,
      UnderlineExt,
      Link.configure({
        openOnClick: false,
      }),
      TextAlign.configure({
        types: ['heading', 'paragraph'],
      }),
    ],
    content: content || '',
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    },
  });

  // Update content when it changes externally
  useEffect(() => {
    if (editor && content !== editor.getHTML()) {
      editor.commands.setContent(content || '');
    }
  }, [content, editor]);

  const setLink = useCallback(() => {
    if (!editor) return;
    const previousUrl = editor.getAttributes('link').href;
    const url = window.prompt('URL eingeben:', previousUrl);
    if (url === null) return;
    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
  }, [editor]);

  if (!editor) {
    return <div className="border rounded-lg p-4 min-h-[200px] bg-gray-50">Laden...</div>;
  }

  return (
    <div className="border rounded-lg overflow-hidden">
      {/* Toolbar */}
      <div className="flex flex-wrap gap-1 p-2 border-b bg-gray-50">
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleBold().run()}
          className={`p-2 rounded hover:bg-gray-200 ${editor.isActive('bold') ? 'bg-gray-200' : ''}`}
          title="Fett"
        >
          <Bold className="w-4 h-4" />
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleItalic().run()}
          className={`p-2 rounded hover:bg-gray-200 ${editor.isActive('italic') ? 'bg-gray-200' : ''}`}
          title="Kursiv"
        >
          <Italic className="w-4 h-4" />
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleUnderline().run()}
          className={`p-2 rounded hover:bg-gray-200 ${editor.isActive('underline') ? 'bg-gray-200' : ''}`}
          title="Unterstrichen"
        >
          <Underline className="w-4 h-4" />
        </button>
        <div className="w-px bg-gray-300 mx-1" />
        <button
          type="button"
          onClick={() => editor.chain().focus().setTextAlign('left').run()}
          className={`p-2 rounded hover:bg-gray-200 ${editor.isActive({ textAlign: 'left' }) ? 'bg-gray-200' : ''}`}
          title="Linksbündig"
        >
          <AlignLeft className="w-4 h-4" />
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().setTextAlign('center').run()}
          className={`p-2 rounded hover:bg-gray-200 ${editor.isActive({ textAlign: 'center' }) ? 'bg-gray-200' : ''}`}
          title="Zentriert"
        >
          <AlignCenter className="w-4 h-4" />
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().setTextAlign('right').run()}
          className={`p-2 rounded hover:bg-gray-200 ${editor.isActive({ textAlign: 'right' }) ? 'bg-gray-200' : ''}`}
          title="Rechtsbündig"
        >
          <AlignRight className="w-4 h-4" />
        </button>
        <div className="w-px bg-gray-300 mx-1" />
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          className={`p-2 rounded hover:bg-gray-200 ${editor.isActive('bulletList') ? 'bg-gray-200' : ''}`}
          title="Aufzählung"
        >
          <List className="w-4 h-4" />
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          className={`p-2 rounded hover:bg-gray-200 ${editor.isActive('orderedList') ? 'bg-gray-200' : ''}`}
          title="Nummerierte Liste"
        >
          <ListOrdered className="w-4 h-4" />
        </button>
        <div className="w-px bg-gray-300 mx-1" />
        <button
          type="button"
          onClick={setLink}
          className={`p-2 rounded hover:bg-gray-200 ${editor.isActive('link') ? 'bg-gray-200' : ''}`}
          title="Link"
        >
          <Link2 className="w-4 h-4" />
        </button>
      </div>
      {/* Editor Content */}
      <EditorContent 
        editor={editor} 
        className="prose prose-sm max-w-none p-4 min-h-[200px] focus:outline-none"
      />
    </div>
  );
};

const PopupManagement = () => {
  const { token, user } = useAuth();
  const [popups, setPopups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingPopup, setEditingPopup] = useState(null);
  const [activeTab, setActiveTab] = useState('all');
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    start_date: '',
    end_date: '',
    is_active: true,
    notification_email: ''
  });

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const fetchPopups = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/popups`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setPopups(data);
      }
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Failed to fetch popups:', error);
      }
    } finally {
      setLoading(false);
    }
  }, [API_URL, token]);

  useEffect(() => {
    fetchPopups();
  }, [fetchPopups]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const url = editingPopup 
      ? `${API_URL}/api/admin/popups/${editingPopup.id}`
      : `${API_URL}/api/admin/popups`;
    
    const method = editingPopup ? 'PUT' : 'POST';

    try {
      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...formData,
          start_date: new Date(formData.start_date).toISOString(),
          end_date: new Date(formData.end_date).toISOString()
        })
      });

      if (response.ok) {
        fetchPopups();
        closeModal();
      }
    } catch (error) {
      console.error('Failed to save popup:', error);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Popup wirklich löschen?')) return;

    try {
      await fetch(`${API_URL}/api/admin/popups/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      fetchPopups();
    } catch (error) {
      console.error('Failed to delete popup:', error);
    }
  };

  const openEditModal = (popup) => {
    setEditingPopup(popup);
    const startDate = popup.start_date ? new Date(popup.start_date) : new Date();
    const endDate = popup.end_date ? new Date(popup.end_date) : new Date();
    
    setFormData({
      title: popup.title,
      content: popup.content,
      start_date: format(startDate, "yyyy-MM-dd'T'HH:mm"),
      end_date: format(endDate, "yyyy-MM-dd'T'HH:mm"),
      is_active: popup.is_active,
      notification_email: popup.notification_email || ''
    });
    setShowModal(true);
  };

  const openNewModal = () => {
    setEditingPopup(null);
    setFormData({
      title: '',
      content: '',
      start_date: '',
      end_date: '',
      is_active: true,
      notification_email: ''
    });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingPopup(null);
    setFormData({
      title: '',
      content: '',
      start_date: '',
      end_date: '',
      is_active: true,
      notification_email: ''
    });
  };

  const getStatusBadge = (status) => {
    const styles = {
      active: { bg: 'bg-green-100', text: 'text-green-700', icon: CheckCircle, label: 'Aktiv' },
      scheduled: { bg: 'bg-blue-100', text: 'text-blue-700', icon: Clock, label: 'Geplant' },
      expired: { bg: 'bg-gray-100', text: 'text-gray-700', icon: XCircle, label: 'Abgelaufen' }
    };
    const style = styles[status] || styles.scheduled;
    const Icon = style.icon;
    
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs ${style.bg} ${style.text}`}>
        <Icon className="w-3 h-3" />
        {style.label}
      </span>
    );
  };

  const filteredPopups = popups.filter(popup => {
    if (activeTab === 'all') return true;
    return popup.status === activeTab;
  });

  const formatDate = (dateStr) => {
    try {
      return format(parseISO(dateStr), 'dd.MM.yyyy HH:mm', { locale: de });
    } catch {
      return dateStr;
    }
  };

  if (loading) {
    return <div className="flex justify-center items-center h-64">Laden...</div>;
  }

  return (
    <div>
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold" style={{ color: '#1B4D3E' }}>Popup-Fenster</h1>
          <p className="text-gray-500">Ankündigungen planen und verwalten</p>
        </div>
        <Button
          onClick={openNewModal}
          style={{ backgroundColor: '#1B4D3E' }}
        >
          <Plus className="w-4 h-4 mr-2" />
          Neues Popup
        </Button>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
        {[
          { key: 'all', label: 'Alle' },
          { key: 'active', label: 'Aktiv' },
          { key: 'scheduled', label: 'Geplant' },
          { key: 'expired', label: 'Abgelaufen' }
        ].map(tab => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`px-4 py-2 rounded-lg whitespace-nowrap ${
              activeTab === tab.key 
                ? 'text-white' 
                : 'bg-white text-gray-600 hover:bg-gray-100'
            }`}
            style={activeTab === tab.key ? { backgroundColor: '#1B4D3E' } : {}}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-100">
        {filteredPopups.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            Keine Popups gefunden
          </div>
        ) : (
          <div className="divide-y">
            {filteredPopups.map((popup) => (
              <div key={popup.id} className="p-4 hover:bg-gray-50">
                <div className="flex items-start gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-semibold">{popup.title}</h3>
                      {getStatusBadge(popup.status)}
                    </div>
                    <div className="flex flex-wrap gap-4 text-sm text-gray-500">
                      <span className="flex items-center gap-1">
                        <Calendar className="w-4 h-4" />
                        {formatDate(popup.start_date)} - {formatDate(popup.end_date)}
                      </span>
                      {popup.notification_email && (
                        <span className="flex items-center gap-1">
                          <Mail className="w-4 h-4" />
                          {popup.notification_email}
                        </span>
                      )}
                    </div>
                    <div 
                      className="mt-2 text-sm text-gray-600 line-clamp-2"
                      dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(popup.content) }}
                    />
                  </div>

                  <div className="flex gap-2 flex-shrink-0">
                    <button
                      onClick={() => openEditModal(popup)}
                      className="p-2 hover:bg-gray-200 rounded-lg"
                    >
                      <Pencil className="w-4 h-4" style={{ color: '#1B4D3E' }} />
                    </button>
                    <button
                      onClick={() => handleDelete(popup.id)}
                      className="p-2 hover:bg-red-100 rounded-lg"
                    >
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 w-full max-w-3xl max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-bold mb-4" style={{ color: '#1B4D3E' }}>
              {editingPopup ? 'Popup bearbeiten' : 'Neues Popup'}
            </h2>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Titel</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                  className="w-full px-4 py-2 border rounded-lg"
                  placeholder="z.B. Praxisschließung, Urlaubsankündigung"
                  required
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Startdatum & Uhrzeit</label>
                  <input
                    type="datetime-local"
                    value={formData.start_date}
                    onChange={(e) => setFormData(prev => ({ ...prev, start_date: e.target.value }))}
                    className="w-full px-4 py-2 border rounded-lg"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Enddatum & Uhrzeit</label>
                  <input
                    type="datetime-local"
                    value={formData.end_date}
                    onChange={(e) => setFormData(prev => ({ ...prev, end_date: e.target.value }))}
                    className="w-full px-4 py-2 border rounded-lg"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">
                  Benachrichtigungs-E-Mail (wenn Popup aktiv wird)
                </label>
                <input
                  type="email"
                  value={formData.notification_email}
                  onChange={(e) => setFormData(prev => ({ ...prev, notification_email: e.target.value }))}
                  className="w-full px-4 py-2 border rounded-lg"
                  placeholder="optional@email.de"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Inhalt</label>
                <RichTextEditor
                  content={formData.content}
                  onChange={(content) => setFormData(prev => ({ ...prev, content }))}
                />
                <p className="text-xs text-gray-500 mt-1">
                  Formatieren Sie den Text mit den Werkzeugen oben (Fett, Kursiv, Unterstrichen, Ausrichtung, Listen, Links)
                </p>
              </div>

              <div className="flex items-center gap-2 pt-2">
                <input
                  type="checkbox"
                  id="is_active"
                  checked={formData.is_active}
                  onChange={(e) => setFormData(prev => ({ ...prev, is_active: e.target.checked }))}
                  className="rounded"
                />
                <label htmlFor="is_active" className="text-sm">
                  Popup aktivieren (wird im Zeitraum angezeigt)
                </label>
              </div>

              <div className="flex gap-4 pt-4">
                <Button type="button" variant="outline" onClick={closeModal} className="flex-1">
                  Abbrechen
                </Button>
                <Button type="submit" className="flex-1" style={{ backgroundColor: '#1B4D3E' }}>
                  Speichern
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      <style>{`
        .ProseMirror {
          min-height: 150px;
          outline: none;
        }
        .ProseMirror p {
          margin: 0.5em 0;
        }
        .ProseMirror ul, .ProseMirror ol {
          padding-left: 1.5em;
        }
        .ProseMirror a {
          color: #1B4D3E;
          text-decoration: underline;
        }
      `}</style>
    </div>
  );
};

export default PopupManagement;
